import { BitkitAlert, BitkitTabs } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import { segmentTrack, segmentTrackOnce } from '@/hooks/analytics/useTracking';
import useQueryParams from '@/hooks/useQueryParams';
import useTabsQueryParam from '@/hooks/useTabsQueryParam';
import { BuildCacheInfo } from '@/models/BuildCacheInfo';
import { BuildCacheToolIds, buildCacheTools } from '@/models/BuildCacheTool';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import InvocationList from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList';
import { INVOCATIONS_SELECTED_TOOL_COOKIE } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/constants';
import { getTabList } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/utils/buildToolTabList';
import { getCookie, setCookie } from '@/utils/cookies';

import useBuildInvocations from '../hooks/useBuildInvocations';
import { SuboptimalGradleCacheGuideInfoBox } from '../SuboptimalGradleCacheGuideInfoBox';

export type BuildInvocationsProps = {
  buildSlug: string;
  buildCacheInfo: BuildCacheInfo;
};

type ToolInvocationsProps = {
  buildSlug: string;
  selectedToolId: BuildCacheToolIds;
};

const ToolInvocations = ({ buildSlug, selectedToolId }: ToolInvocationsProps) => {
  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const {
    data: responseData,
    isError: isInvocationsError,
    onPageChange: setPage,
    onPerPageChange: setPerPage,
  } = useBuildInvocations(workspaceSlug, buildSlug, selectedToolId);

  const selectedTool = buildCacheTools[selectedToolId];

  if (isInvocationsError) {
    return <BitkitAlert variant="critical" messageText="Failed to load invocations" />;
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap="4" borderRadius="4" paddingBlockEnd="24">
        <Text textStyle="heading/h2" color="text/primary">
          Invocations
        </Text>
        <Text textStyle="body/lg/regular" color="text/secondary">
          All instances when {selectedTool.name} was triggered.
        </Text>
      </Box>
      <InvocationList
        isBuildSpecific
        isFiltered={false}
        invocationsResponseData={responseData}
        setPage={setPage}
        setPerPage={setPerPage}
        selectedTool={selectedTool.id}
      />
    </>
  );
};

const BuildInvocations = ({ buildSlug, buildCacheInfo }: BuildInvocationsProps) => {
  const {
    account: { slug: accountSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const queryParams = useQueryParams();
  const primaryTabParam = queryParams.get('tab');

  const availableTabList = useMemo(() => getTabList(), []);

  const cookieTool = getCookie<BuildCacheToolIds>(INVOCATIONS_SELECTED_TOOL_COOKIE);

  const initialTool = useMemo(() => {
    const hasInvocationsFor = (id: BuildCacheToolIds) =>
      buildCacheInfo.invocations?.some(({ tool }) => tool === id) ?? false;

    // The remembered tool only wins when this build actually ran it, otherwise the preference would
    // open an empty tab on a build that has invocations from another tool.
    return (
      availableTabList.find(({ id }) => id === cookieTool && hasInvocationsFor(id)) ??
      availableTabList.find(({ id }) => id !== BuildCacheToolIds.gradle && hasInvocationsFor(id)) ??
      availableTabList.find(({ id }) => id === BuildCacheToolIds.gradle) ??
      availableTabList[0]
    );
  }, [availableTabList, buildCacheInfo.invocations, cookieTool]);

  const { selectTab, tabId } = useTabsQueryParam<BuildCacheToolIds>({
    queryKey: 'secondary_tab',
    defaultId: initialTool.id,
    tabIds: availableTabList.map(({ id }) => id),
  });
  const selectedTool = tabId && availableTabList.some(({ id }) => id === tabId) ? tabId : initialTool.id;

  const initiallySelectedTool = useRef(selectedTool);
  useEffect(() => {
    if (primaryTabParam === BuildDetailsTabId.buildCache) {
      segmentTrackOnce('Build Cache Operations Tab Displayed', {
        build_slug: buildSlug,
        is_default_tab: true,
        tab_name: initiallySelectedTool.current,
        workspace_slug: accountSlug,
      });
    }
  }, [accountSlug, buildSlug, primaryTabParam]);

  const onTabChange = useCallback(
    (value: string) => {
      const id = availableTabList.find((tab) => tab.id === value)?.id;
      if (!id) {
        return;
      }

      selectTab(id);

      if (id !== selectedTool) {
        setCookie(INVOCATIONS_SELECTED_TOOL_COOKIE, id, { cookieOptions: { expires: 365 } });
      }

      if (id !== tabId) {
        segmentTrack('Build Cache Operations Tab Displayed', {
          build_slug: buildSlug,
          is_default_tab: false,
          tab_name: id,
          workspace_slug: accountSlug,
        });
      }
    },
    [accountSlug, availableTabList, buildSlug, selectTab, selectedTool, tabId],
  );

  return (
    <Box display="flex" flexDirection="column" gap="32">
      <BitkitTabs.Root
        onValueChange={({ value }) => onTabChange(value)}
        value={selectedTool}
        variant="contained"
        width="100%"
      >
        <BitkitTabs.List>
          {availableTabList.map(({ id, title, iconComponent }) => (
            <BitkitTabs.Trigger icon={iconComponent} key={id} value={id}>
              {title}
            </BitkitTabs.Trigger>
          ))}
        </BitkitTabs.List>
        <BitkitTabs.ContentGroup paddingBlock="32" paddingInline={['8', '32']}>
          {availableTabList.map(({ id: toolId }) => (
            <BitkitTabs.Content key={toolId} value={toolId}>
              {toolId === selectedTool && <ToolInvocations buildSlug={buildSlug} selectedToolId={selectedTool} />}
            </BitkitTabs.Content>
          ))}
        </BitkitTabs.ContentGroup>
      </BitkitTabs.Root>
      {selectedTool === BuildCacheToolIds.gradle && <SuboptimalGradleCacheGuideInfoBox />}
    </Box>
  );
};

export default BuildInvocations;
